import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, X, BookOpen, PlusCircle, MinusCircle, ArrowRightLeft, Pencil, Check, ScrollText } from 'lucide-react';
import JournalVersionsPanel from '../components/JournalVersionsPanel';
import ReopenActivityDrawer from '../components/ReopenActivityDrawer';
import { useFocusEntity } from '../hooks/useFocusEntity';

const Journal = ({ user }) => {
  const navigate = useNavigate();
  const { focusedId, registerRow } = useFocusEntity();
  const [journals, setJournals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [expandedJournal, setExpandedJournal] = useState(null);
  // Per-line inline edit state: { [journalId__lineIdx]: pendingProjectId }
  const [editingLine, setEditingLine] = useState(null);
  const [editingProjectId, setEditingProjectId] = useState('');
  const [savingLine, setSavingLine] = useState(false);
  // Reopen Activity Drawer — opened from the "Last Change" column.
  const [activityJournalId, setActivityJournalId] = useState(null);
  // Last-change summaries keyed by journal_id (fetched lazily per page render).
  const [lastChangeMap, setLastChangeMap] = useState({});

  // Auto-expand the row that the Audit Stream / Bank page navigated us to.
  // Mirrors the focus-highlight pulse so the user lands on a row that's
  // already showing its full context, not just a glowing line.
  useEffect(() => {
    if (focusedId) setExpandedJournal(focusedId);
  }, [focusedId]);
  
  // Only Admin can delete journal entries
  const canDelete = user?.role === 'admin';
  
  const [formData, setFormData] = useState({
    journal_date: '',
    reference: '',
    description: '',
    project_id: '',
    lines: [
      { account_id: '', account_name: '', debit: 0, credit: 0, description: '' },
      { account_id: '', account_name: '', debit: 0, credit: 0, description: '' }
    ]
  });

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [journalsRes, accountsRes, projectsRes, lastChangesRes] = await Promise.all([
        axiosInstance.get('/journals'),
        axiosInstance.get('/accounts'),
        axiosInstance.get('/projects'),
        // Best-effort: column degrades to "—" if this fails.
        axiosInstance.get('/journals/last-changes/batch').catch(() => ({ data: { by_journal_id: {} } })),
      ]);
      setJournals(journalsRes.data);
      setAccounts(accountsRes.data.filter(a => a.status === 'active'));
      setProjects(projectsRes.data);
      setLastChangeMap(lastChangesRes.data?.by_journal_id || {});
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (lines) => {
    const totalDebit = lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const totalCredit = lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    return { totalDebit, totalCredit };
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    if (field === 'account_id') {
      const selectedAccount = accounts.find(a => a.id === value);
      newLines[index] = {
        ...newLines[index],
        account_id: value,
        account_name: selectedAccount?.name || ''
      };
    } else if (field === 'debit' || field === 'credit') {
      newLines[index][field] = parseFloat(value) || 0;
    } else {
      newLines[index][field] = value;
    }
    setFormData({ ...formData, lines: newLines });
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { account_id: '', account_name: '', debit: 0, credit: 0, description: '' }]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length <= 2) {
      toast.error('Journal entry must have at least 2 lines');
      return;
    }
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData({ ...formData, lines: newLines });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const { totalDebit, totalCredit } = calculateTotals(formData.lines);
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error(`Journal not balanced! Debit: ${totalDebit.toFixed(2)} SAR, Credit: ${totalCredit.toFixed(2)} SAR`);
      return;
    }

    const emptyLines = formData.lines.filter(line => !line.account_id);
    if (emptyLines.length > 0) {
      toast.error('Please select an account for all lines');
      return;
    }

    const zeroLines = formData.lines.filter(line => line.debit === 0 && line.credit === 0);
    if (zeroLines.length > 0) {
      toast.error('Each line must have either a debit or credit amount');
      return;
    }

    try {
      await axiosInstance.post('/journals', formData);
      toast.success('Journal entry created successfully');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create journal entry');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this journal entry?')) {
      try {
        await axiosInstance.delete(`/journals/${id}`);
        toast.success('Journal entry deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete journal entry');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      journal_date: '',
      reference: '',
      description: '',
      project_id: '',
      lines: [
        { account_id: '', account_name: '', debit: 0, credit: 0, description: '' },
        { account_id: '', account_name: '', debit: 0, credit: 0, description: '' }
      ]
    });
  };

  const { totalDebit: formTotalDebit, totalCredit: formTotalCredit } = calculateTotals(formData.lines);
  const isBalanced = Math.abs(formTotalDebit - formTotalCredit) < 0.01;

  const totalJournalDebit = journals.reduce((sum, j) => sum + j.total_debit, 0);
  const totalJournalCredit = journals.reduce((sum, j) => sum + j.total_credit, 0);

  if (loading) {
    return <div className="text-center py-12">Loading journals...</div>;
  }

  return (
    <div data-testid="journal-page">
      <div className="page-header">
        <h2 className="page-title">Journal Entries</h2>
        <button
          data-testid="add-journal-button"
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Journal Entry
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon purple">
            <BookOpen className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Entries</h3>
          <p className="text-3xl font-bold text-gray-900">{journals.length}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <PlusCircle className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Debit</h3>
          <p className="text-3xl font-bold text-green-600">{totalJournalDebit.toLocaleString()} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <MinusCircle className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Credit</h3>
          <p className="text-3xl font-bold text-red-600">{totalJournalCredit.toLocaleString()} SAR</p>
        </div>
      </div>

      {/* Journals Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Journal No</th>
              <th>Date</th>
              <th>Reference</th>
              <th>Description</th>
              <th>Total Debit</th>
              <th>Total Credit</th>
              <th>Project</th>
              <th>User</th>
              <th data-testid="journal-th-last-change">Last Change</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {journals.length > 0 ? (
              journals.map((journal) => (
                <React.Fragment key={journal.id}>
                  <tr 
                    ref={registerRow(journal.id)}
                    data-testid={`journal-row-${journal.id}`}
                    className={`cursor-pointer hover:bg-purple-50 ${focusedId === journal.id ? 'focus-highlight' : ''}`}
                    onClick={() => setExpandedJournal(expandedJournal === journal.id ? null : journal.id)}
                  >
                    <td className="font-semibold text-purple-600">{journal.journal_no}</td>
                    <td>{new Date(journal.journal_date).toLocaleDateString()}</td>
                    <td>{journal.reference || '-'}</td>
                    <td className="max-w-xs truncate">{journal.description}</td>
                    <td className="font-bold text-green-600">{journal.total_debit.toLocaleString()} SAR</td>
                    <td className="font-bold text-red-600">{journal.total_credit.toLocaleString()} SAR</td>
                    <td data-testid={`journal-project-${journal.id}`}>
                      {(() => {
                        // Derive Project column from line-level project assignments
                        // so it reflects per-line edits in real time. Fallback to the
                        // journal-level field for legacy rows with no line projects.
                        const lineProjects = (journal.lines || [])
                          .filter((l) => l.project_id)
                          .map((l) => l.project_name ||
                            projects.find((p) => p.id === l.project_id)?.project_name ||
                            projects.find((p) => p.id === l.project_id)?.name ||
                            l.project_id);
                        const unique = [...new Set(lineProjects)];
                        if (unique.length === 0) return journal.project_name || 'N/A';
                        if (unique.length === 1) return unique[0];
                        return (
                          <span className="text-amber-600 text-xs font-medium" title={unique.join(', ')}>
                            Multiple ({unique.length})
                          </span>
                        );
                      })()}
                    </td>
                    <td>{journal.user_name}</td>
                    <td data-testid={`journal-last-change-${journal.id}`}>
                      {(() => {
                        const lc = lastChangeMap[journal.id];
                        const inReopenWindow =
                          journal.created_during_reopen ||
                          journal.last_amended_during_reopen;
                        // Always render a clickable affordance — Replay
                        // tab works for every journal (even pristine
                        // ones show Created + Current events).
                        const fc = lc?.field_changes?.[0];
                        const linesFc = lc?.field_changes?.find(c => c.key === 'lines');
                        return (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setActivityJournalId(journal.id); }}
                            className="flex items-start gap-1.5 text-left hover:underline"
                            data-testid={`journal-last-change-btn-${journal.id}`}
                            title="Open Reopen Activity / Audit Replay"
                          >
                            <ScrollText className="w-3.5 h-3.5 mt-0.5 text-purple-600 flex-shrink-0" />
                            <span className="text-xs">
                              {lc ? (
                                <>
                                  <span className="font-semibold text-slate-700">
                                    v{lc.version_number}
                                  </span>
                                  {fc && fc.key !== 'lines' && (
                                    <span className="block text-slate-500 max-w-[180px] truncate">
                                      {fc.key}: <span className="line-through text-rose-500">{String(fc.before ?? '—').slice(0, 18)}</span> → <span className="text-emerald-600">{String(fc.after ?? '—').slice(0, 18)}</span>
                                    </span>
                                  )}
                                  {linesFc && linesFc.summary && (
                                    <span className="block text-slate-500">
                                      Δ {linesFc.summary.total_debit_delta >= 0 ? '+' : ''}{linesFc.summary.total_debit_delta} SAR
                                    </span>
                                  )}
                                </>
                              ) : inReopenWindow ? (
                                <span className="text-purple-700 font-medium">In Reopen</span>
                              ) : (
                                <span className="text-slate-400">Replay</span>
                              )}
                            </span>
                          </button>
                        );
                      })()}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {canDelete && (
                          <button
                            data-testid={`delete-journal-${journal.id}`}
                            onClick={(e) => { e.stopPropagation(); handleDelete(journal.id); }}
                            className="btn-icon delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded Lines */}
                  {expandedJournal === journal.id && (
                    <tr>
                      <td colSpan="10" className="bg-gray-50 p-4">
                        {/* Phase 7.x · Bank → Journal Navigation (reverse).
                            When this journal was emitted by a bank
                            transaction, surface the source + a button
                            to jump back to it. Same affordance as the
                            Bank → Journal direction, just inverted. */}
                        {journal.reference_type === 'bank_transaction' && journal.reference_id && (
                          <div
                            className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center justify-between gap-3"
                            data-testid={`journal-source-bank-${journal.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <ArrowRightLeft className="w-4 h-4 text-indigo-600" />
                              <div>
                                <p className="text-xs text-indigo-600 font-medium">
                                  أُنشئ من حركة بنكية
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  مرجع:{' '}
                                  <span className="font-mono">{journal.reference}</span>
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              data-testid="open-source-bank-tx-btn"
                              onClick={() => navigate(`/bank?focus=${journal.reference_id}`)}
                              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors flex items-center gap-1"
                            >
                              <ArrowRightLeft className="w-3.5 h-3.5" />
                              افتح الحركة البنكية
                            </button>
                          </div>
                        )}

                        {/* P15-EXP1 · Journal → Expense Report drill-down.
                            When the journal was emitted by approving an
                            Expense Report, surface a banner with a one-click
                            jump to the report. Matches the bank-transaction
                            pattern above for visual consistency. */}
                        {journal.reference_type === 'expense_report' && journal.reference_id && (
                          <div
                            className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between gap-3"
                            data-testid={`journal-source-expense-report-${journal.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <ArrowRightLeft className="w-4 h-4 text-emerald-600" />
                              <div>
                                <p className="text-xs text-emerald-700 font-medium">
                                  أُنشئ من تقرير مصروفات
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  مرجع:{' '}
                                  <span className="font-mono">{journal.reference}</span>
                                  {journal.unmapped_categories && journal.unmapped_categories.length > 0 && (
                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-medium">
                                      ⚠ {journal.unmapped_categories.length} unmapped
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              data-testid="open-source-expense-report-btn"
                              onClick={() => navigate(`/expenses-report?focus=${journal.reference_id}`)}
                              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors flex items-center gap-1"
                            >
                              <ArrowRightLeft className="w-3.5 h-3.5" />
                              افتح تقرير المصروفات
                            </button>
                          </div>
                        )}
                        <div className="text-sm font-medium text-gray-700 mb-2">Journal Lines:</div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="px-3 py-2 text-left">Account</th>
                              <th className="px-3 py-2 text-left">Description</th>
                              <th className="px-3 py-2 text-left">Project</th>
                              <th className="px-3 py-2 text-right">Debit</th>
                              <th className="px-3 py-2 text-right">Credit</th>
                              <th className="px-3 py-2 text-center w-12"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {journal.lines.map((line, idx) => {
                              const editKey = `${journal.id}__${idx}`;
                              const isEditing = editingLine === editKey;
                              const currentProjectName =
                                line.project_name ||
                                projects.find((p) => p.id === line.project_id)?.project_name ||
                                projects.find((p) => p.id === line.project_id)?.name ||
                                '';
                              return (
                                <tr key={idx} className="border-b border-gray-200" data-testid={`journal-line-${journal.id}-${idx}`}>
                                  <td className="px-3 py-2 font-medium">{line.account_name}</td>
                                  <td className="px-3 py-2 text-gray-600">{line.description || '-'}</td>
                                  <td className="px-3 py-2">
                                    {isEditing ? (
                                      <select
                                        data-testid={`journal-line-project-select-${journal.id}-${idx}`}
                                        value={editingProjectId}
                                        onChange={(e) => setEditingProjectId(e.target.value)}
                                        disabled={savingLine}
                                        className="border rounded px-2 py-1 text-xs min-w-[180px]"
                                      >
                                        <option value="">— No Project —</option>
                                        {projects.map((p) => (
                                          <option key={p.id} value={p.id}>
                                            {p.project_code ? `${p.project_code} — ` : ''}
                                            {p.project_name || p.name}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span
                                        className={`text-xs ${line.project_id ? 'text-purple-700 font-medium' : 'text-gray-400 italic'}`}
                                        data-testid={`journal-line-project-display-${journal.id}-${idx}`}
                                      >
                                        {currentProjectName || '— No project —'}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right text-green-600">
                                    {line.debit > 0 ? `${line.debit.toLocaleString()} SAR` : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-right text-red-600">
                                    {line.credit > 0 ? `${line.credit.toLocaleString()} SAR` : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {isEditing ? (
                                      <div className="flex items-center justify-center gap-1">
                                        <button
                                          data-testid={`journal-line-save-${journal.id}-${idx}`}
                                          disabled={savingLine}
                                          onClick={async () => {
                                            setSavingLine(true);
                                            try {
                                              const res = await axiosInstance.patch(
                                                `/journals/${journal.id}/lines/${idx}/project`,
                                                { project_id: editingProjectId || null },
                                              );
                                              toast.success('Project updated');
                                              // Optimistically update in state.
                                              setJournals((prev) => prev.map((j) =>
                                                j.id === journal.id
                                                  ? { ...j, lines: j.lines.map((l, i) =>
                                                      i === idx
                                                        ? { ...l, project_id: res.data.project_id, project_name: res.data.project_name }
                                                        : l) }
                                                  : j,
                                              ));
                                              setEditingLine(null);
                                              setEditingProjectId('');
                                            } catch (err) {
                                              toast.error(err.response?.data?.detail || 'Failed to update project');
                                            } finally {
                                              setSavingLine(false);
                                            }
                                          }}
                                          className="p-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                          title="Save"
                                        >
                                          <Check className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          data-testid={`journal-line-cancel-${journal.id}-${idx}`}
                                          disabled={savingLine}
                                          onClick={() => { setEditingLine(null); setEditingProjectId(''); }}
                                          className="p-1 rounded border border-gray-300 hover:bg-gray-100"
                                          title="Cancel"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        data-testid={`journal-line-edit-${journal.id}-${idx}`}
                                        onClick={() => {
                                          setEditingLine(editKey);
                                          setEditingProjectId(line.project_id || '');
                                        }}
                                        className="p-1 rounded border border-purple-200 text-purple-600 hover:bg-purple-600 hover:text-white transition"
                                        title="Edit project"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {/* Phase 7.x · P1.2 — Journal Versioning timeline */}
                        <div className="mt-4">
                          <JournalVersionsPanel
                            journal={journal}
                            onUpdated={() => fetchData()}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-12 text-gray-500">
                  No journal entries found. Create your first entry!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New Journal Entry</h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Header Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="form-group">
                  <label className="form-label">Journal Date *</label>
                  <input
                    data-testid="journal-date-input"
                    type="date"
                    value={formData.journal_date}
                    onChange={(e) => setFormData({ ...formData, journal_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Reference</label>
                  <input
                    data-testid="journal-reference-input"
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    className="form-input"
                    placeholder="Invoice/Receipt number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="form-group">
                  <label className="form-label">Description *</label>
                  <input
                    data-testid="journal-description-input"
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="form-input"
                    placeholder="Journal entry description"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Project (Optional)</label>
                  <select
                    data-testid="journal-project-select"
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    className="form-select"
                  >
                    <option value="">No Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Journal Lines */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-3">
                  <label className="form-label mb-0">Journal Lines *</label>
                  <button
                    type="button"
                    onClick={addLine}
                    className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center gap-1"
                  >
                    <PlusCircle className="w-4 h-4" />
                    Add Line
                  </button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Account *</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right w-32">Debit</th>
                        <th className="px-3 py-2 text-right w-32">Credit</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.lines.map((line, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-3 py-2">
                            <select
                              data-testid={`line-account-${index}`}
                              value={line.account_id}
                              onChange={(e) => handleLineChange(index, 'account_id', e.target.value)}
                              className="form-select text-sm"
                              required
                            >
                              <option value="">Select Account</option>
                              {accounts.map(account => (
                                <option key={account.id} value={account.id}>
                                  {account.account_number} - {account.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={line.description}
                              onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                              className="form-input text-sm"
                              placeholder="Line description"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              data-testid={`line-debit-${index}`}
                              type="number"
                              value={line.debit || ''}
                              onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                              className="form-input text-sm text-right"
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              data-testid={`line-credit-${index}`}
                              type="number"
                              value={line.credit || ''}
                              onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                              className="form-input text-sm text-right"
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => removeLine(index)}
                              className="text-red-500 hover:text-red-700"
                              title="Remove line"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-semibold">
                      <tr>
                        <td colSpan="2" className="px-3 py-2 text-right">Totals:</td>
                        <td className="px-3 py-2 text-right text-green-600">
                          {formTotalDebit.toFixed(2)} SAR
                        </td>
                        <td className="px-3 py-2 text-right text-red-600">
                          {formTotalCredit.toFixed(2)} SAR
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Balance Status */}
                <div className={`mt-3 p-3 rounded-lg ${isBalanced ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={`text-sm font-medium ${isBalanced ? 'text-green-700' : 'text-red-700'}`}>
                    {isBalanced 
                      ? '✓ Journal entry is balanced' 
                      : `✗ Not balanced! Difference: ${Math.abs(formTotalDebit - formTotalCredit).toFixed(2)} SAR`
                    }
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-journal-button"
                  type="submit"
                  disabled={!isBalanced}
                  className={`btn-primary flex-1 ${!isBalanced ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Create Journal Entry
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reopen Activity Drawer — opens from the "Last Change" column */}
      {activityJournalId && (
        <ReopenActivityDrawer
          journalId={activityJournalId}
          onClose={() => setActivityJournalId(null)}
        />
      )}
    </div>
  );
};

export default Journal;
